import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import { Toast } from "primereact/toast";
import "../../css/modal.css";
import "../../css/login.css";
import { ImSpinner7 } from "react-icons/im";
import { Context as AuthContext } from "../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;
const LOGO_SRC = "/final-bv-logo.png";

export const Login = () => {
  const { state, userLoggedIn, closeLoginModal, openLoginModal } =
    useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [loginStep, setLoginStep] = useState("method");
  const [authMethod, setAuthMethod] = useState(null);
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [OTP, setOTP] = useState("");
  const [name, setName] = useState("");
  const [showNameField, setShowNameField] = useState(false);
  const [resendCountdown, setResendCountdown] = useState();

  const toast = useRef(null);

  const handleChooseEmail = () => {
    setAuthMethod("email");
    setMobile("");
    setLoginStep("credentials");
  };

  const handleChooseMobile = () => {
    setAuthMethod("mobile");
    setEmail("");
    setLoginStep("credentials");
  };

  const resetLoginState = useCallback(() => {
    setLoginStep("method");
    setAuthMethod(null);
    setMobile("");
    setEmail("");
    setOTP("");
    setName("");
    setShowNameField(false);
    setResendCountdown(60);
  }, []);

  const handleClose = useCallback(() => {
    resetLoginState();
    closeLoginModal();
  }, [resetLoginState, closeLoginModal]);

  const handleBack = () => {
    if (loginStep === "otp") {
      setOTP("");
      setLoginStep("credentials");
      return;
    }
    if (loginStep === "credentials") {
      setMobile("");
      setEmail("");
      setAuthMethod(null);
      setLoginStep("method");
    }
  };

  const showBack = loginStep === "credentials" || loginStep === "otp";

  const minutes = Math.floor(resendCountdown / 60);
  const seconds = resendCountdown % 60;
  const formattedTime = `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;

  const handleChange = (e) => {
    if (e.target.id === "mobile" && /^[0-9]{0,10}$/.test(e.target.value)) {
      setMobile(e.target.value);
    }
    if (e.target.id === "email") {
      setEmail(e.target.value);
    }
    if (e.target.id === "name") {
      setName(e.target.value);
    }
  };

  useEffect(() => {
    resetLoginState();
  }, [state.loginState, resetLoginState]);

  /* Clear leftover Bootstrap modal overlay from older builds */
  useEffect(() => {
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  }, []);

  useEffect(() => {
    if (loginStep === "otp") {
      const firstInput = document.getElementById("otp-0");
      firstInput?.focus();
    }
  }, [loginStep]);

  useEffect(() => {
    if (!state.loginModalOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [state.loginModalOpen, handleClose]);

  useEffect(() => {
    if (location.state?.showLoginModal && !state.userData) {
      openLoginModal();
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: {} },
      );
    }
  }, [
    location.state?.showLoginModal,
    location.pathname,
    location.search,
    state.userData,
    openLoginModal,
    navigate,
  ]);

  useEffect(() => {
    let intervalId;
    if (resendCountdown > 0) {
      intervalId = setInterval(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [resendCountdown]);

  useEffect(() => {
    if (loginStep === "otp" && showNameField) {
      const nameInput = document.getElementById("name");
      if (nameInput) nameInput.focus();
    }
  }, [loginStep, showNameField]);

  const handleResendOTP = () => {
    if (
      (mobile !== "" && /^[0-9]{10}$/.test(mobile)) ||
      (email !== "" && /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email))
    ) {
      if (mobile !== "") {
        sendOTP.mutate({ mobile, url: "/login" });
      } else {
        sendEmailOTP.mutate({ email, url: "/login-via-email" });
      }
    } else {
      setLoginStep("credentials");
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Invalid Mobile No. or Email`,
        life: 2000,
      });
    }
  };

  const sendOTP = useMutation(
    ({ url, ...formData }) =>
      fetch(`${BASE_URL}${url}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      }).then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      }),
    {
      onSuccess: (data) => {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `${data.message}`,
          life: 2000,
        });
        setLoginStep("otp");
        setShowNameField(data.name === null);
        setResendCountdown(300);
      },
      onError: (error) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `${error.message}`,
          life: 2000,
        });
      },
    },
  );

  const sendEmailOTP = useMutation(
    ({ url, ...formData }) =>
      fetch(`${BASE_URL}${url}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      }).then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      }),
    {
      onSuccess: (data) => {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `${data.message}`,
          life: 2000,
        });
        setLoginStep("otp");
        setShowNameField(data.name === null);
        setResendCountdown(60);
      },
      onError: (error) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `${error.message}`,
          life: 2000,
        });
      },
    },
  );

  const verifyOTPAndSaveName = useMutation(
    ({ url, ...formData }) =>
      fetch(`${BASE_URL}${url}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      }).then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      }),
    {
      onSuccess: (data) => {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `${data.message}`,
          life: 2000,
        });
        resetLoginState();
        closeLoginModal();
        userLoggedIn({ token: data.token });
      },
      onError: () => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Invalid OTP. Please try again.",
          life: 2000,
        });
      },
    },
  );

  const handleLogin = () => {
    if (
      (mobile && /^[0-9]{10}$/.test(mobile)) ||
      (email && /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email))
    ) {
      if (mobile) sendOTP.mutate({ mobile, url: "/login" });
      else sendEmailOTP.mutate({ email, url: "/login-via-email" });
    } else {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Invalid Mobile No. or Email`,
        life: 2000,
      });
    }
  };

  const handleOTP = () => {
    if (
      (mobile && /^[0-9]{10}$/.test(mobile)) ||
      (email && /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email))
    ) {
      if (OTP.length === 6) {
        if (showNameField && (!name.trim() || name.length < 2)) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "Please enter a valid name (at least 2 characters)",
            life: 2000,
          });
          return;
        }
        if (mobile) {
          verifyOTPAndSaveName.mutate({
            mobile,
            otp: OTP,
            name: showNameField ? name : undefined,
            url: "/verify-otp",
          });
        } else {
          verifyOTPAndSaveName.mutate({
            email,
            otp: OTP,
            name: showNameField ? name : undefined,
            url: "/verify-otp-via-email",
          });
        }
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Invalid OTP.`,
          life: 2000,
        });
      }
    }
  };

  const stepSubtitle =
    loginStep === "method"
      ? "Choose how you’d like to sign in or create an account"
      : loginStep === "credentials"
        ? authMethod === "mobile"
          ? "Enter your 10-digit mobile number to receive an OTP"
          : "Enter your email address to receive an OTP"
        : authMethod === "mobile"
          ? `Enter the 6-digit code sent to +91 ${mobile}`
          : `Enter the 6-digit code sent to ${email}`;

  const canContinue =
    loginStep === "credentials" &&
    ((authMethod === "mobile" && mobile.length === 10) ||
      (authMethod === "email" && email.length > 0));

  const isSendingOtp = sendOTP.isLoading || sendEmailOTP.isLoading;

  const modalOpen = state.loginModalOpen && !state.userData;

  const modalContent = modalOpen ? (
    <div
      className="bv-auth-overlay login-modal-container"
      role="presentation"
      onClick={handleClose}
    >
      <div
        className="bv-auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bv-login-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bv-login-modal">
          <div className="bv-login-modal__toolbar">
            {showBack ? (
              <button
                type="button"
                className="bv-login-modal__back"
                onClick={handleBack}
                aria-label="Go back"
              >
                <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                <span>Back</span>
              </button>
            ) : (
              <span className="bv-login-modal__toolbar-spacer" aria-hidden="true" />
            )}
            <button
              type="button"
              className="bv-login-modal__close"
              aria-label="Close"
              onClick={handleClose}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>

          <div className="bv-login-modal__brand">
            <img
              src={LOGO_SRC}
              alt="Bookvenue"
              className="bv-login-modal__logo"
            />
          </div>

          <div className="bv-login-modal__intro">
            <h2 id="bv-login-modal-title" className="bv-login-modal__title">
              {loginStep === "otp" ? "Verify OTP" : "Login / Sign up"}
            </h2>
            <p className="bv-login-modal__subtitle">{stepSubtitle}</p>
          </div>

          <div className="bv-login-modal__body">
            {loginStep === "method" && (
              <div className="bv-login-modal__methods">
                <button
                  type="button"
                  className="bv-login-method-card"
                  onClick={handleChooseMobile}
                  disabled={sendOTP.isLoading}
                >
                  <span className="bv-login-method-card__icon" aria-hidden="true">
                    <i className="fa-solid fa-mobile-screen-button" />
                  </span>
                  <span className="bv-login-method-card__text">
                    <span className="bv-login-method-card__label">
                      Continue with Mobile
                    </span>
                    <span className="bv-login-method-card__hint">
                      OTP on your phone
                    </span>
                  </span>
                  <i
                    className="fa-solid fa-chevron-right bv-login-method-card__arrow"
                    aria-hidden="true"
                  />
                </button>

                <button
                  type="button"
                  className="bv-login-method-card"
                  onClick={handleChooseEmail}
                  disabled={sendEmailOTP.isLoading}
                >
                  <span className="bv-login-method-card__icon" aria-hidden="true">
                    <i className="fa-solid fa-envelope" />
                  </span>
                  <span className="bv-login-method-card__text">
                    <span className="bv-login-method-card__label">
                      Continue with Email
                    </span>
                    <span className="bv-login-method-card__hint">
                      OTP in your inbox
                    </span>
                  </span>
                  <i
                    className="fa-solid fa-chevron-right bv-login-method-card__arrow"
                    aria-hidden="true"
                  />
                </button>
              </div>
            )}

            {loginStep === "credentials" && authMethod === "email" && (
              <div className="bv-login-modal__field">
                <label htmlFor="email" className="bv-login-modal__label">
                  Email address
                </label>
                <div className="bv-login-input-wrap">
                  <span className="bv-login-input-wrap__prefix" aria-hidden="true">
                    <i className="fa-regular fa-envelope" />
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={handleChange}
                    className="bv-login-input"
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>
            )}

            {loginStep === "credentials" && authMethod === "mobile" && (
              <div className="bv-login-modal__field">
                <label htmlFor="mobile" className="bv-login-modal__label">
                  Mobile number
                </label>
                <div className="bv-login-input-wrap">
                  <span className="bv-login-input-wrap__prefix">+91</span>
                  <input
                    id="mobile"
                    type="tel"
                    inputMode="numeric"
                    value={mobile}
                    onChange={handleChange}
                    className="bv-login-input"
                    placeholder="10-digit number"
                    autoComplete="tel"
                    maxLength={10}
                    autoFocus
                  />
                </div>
              </div>
            )}

            {loginStep === "credentials" && (
              <button
                type="button"
                className="bv-btn bv-btn--primary bv-login-modal__submit"
                onClick={handleLogin}
                disabled={!canContinue || isSendingOtp}
              >
                {isSendingOtp ? (
                  <>
                    Sending OTP <ImSpinner7 className="pi-spin ms-2" />
                  </>
                ) : (
                  "Get OTP"
                )}
              </button>
            )}

            {loginStep === "otp" && (
              <div className="bv-login-modal__otp">
                {showNameField && (
                  <div className="bv-login-modal__field">
                    <label htmlFor="name" className="bv-login-modal__label">
                      Full name
                    </label>
                    <div className="bv-login-input-wrap">
                      <span className="bv-login-input-wrap__prefix" aria-hidden="true">
                        <i className="fa-regular fa-user" />
                      </span>
                      <input
                        id="name"
                        type="text"
                        value={name}
                        onChange={handleChange}
                        className="bv-login-input"
                        placeholder="Your name"
                        autoComplete="name"
                      />
                    </div>
                  </div>
                )}

                <div
                  className="otp-input-wrapper bv-login-modal__otp-inputs"
                  role="group"
                  aria-label="One-time password"
                >
                  {Array.from({ length: 6 }).map((_, index) => (
                    <input
                      key={index}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength="1"
                      className="otp-input bv-login-otp-digit"
                      value={OTP[index] || ""}
                      id={`otp-${index}`}
                      aria-label={`Digit ${index + 1}`}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^[0-9]$/.test(value) || value === "") {
                          const otpArray = OTP.split("");
                          otpArray[index] = value;
                          const newOTP = otpArray.join("").padEnd(6, "");
                          setOTP(newOTP);
                          if (value && index < 5) {
                            document.getElementById(`otp-${index + 1}`)?.focus();
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !OTP[index] && index > 0) {
                          document.getElementById(`otp-${index - 1}`)?.focus();
                        }
                      }}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  className="bv-btn bv-btn--primary bv-login-modal__submit"
                  onClick={handleOTP}
                  disabled={verifyOTPAndSaveName.isLoading || OTP.length < 6}
                >
                  {verifyOTPAndSaveName.isLoading ? (
                    <>
                      Verifying <ImSpinner7 className="pi-spin ms-2" />
                    </>
                  ) : (
                    "Verify & continue"
                  )}
                </button>

                <div className="bv-login-modal__resend">
                  {resendCountdown > 0 ? (
                    <span className="bv-login-modal__resend-timer">
                      Resend OTP in {formattedTime}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="bv-login-modal__resend-btn"
                      onClick={handleResendOTP}
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <p className="bv-login-modal__footer">
            By continuing, you agree to Bookvenue&apos;s terms and privacy policy.
          </p>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <Toast ref={toast} position="bottom-right" />
      {modalOpen && createPortal(modalContent, document.body)}
    </>
  );
};
